import { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext } from 'react-router-dom';
import BadgeSquare from 'components/badge/badgeSquare';
import useModal from 'components/modal/useModal';
import useSlideOver from 'components/SlideOver/useSlideOver';
import UseContext from 'hooks/useContext';
import { Dots } from 'assets/icons';
import ApiDatabase from 'server';
import { toast } from 'react-toastify';

const STATUS_BADGE_MAP = {
  paid: 14,
  pending: 20,
  partially_paid: 16,
  late: 17,
  cancelled: 18,
  draft: 19,
  to_import_on_pennylane: 21,
};

const TbodyInvoiceAdmin = ({ champsBody }) => {
  return (
    <>
      {champsBody?.map((item, index) => (
        <InvoiceRow
          key={`${item.invoice_number}-${index}`}
          item={item}
        />
      ))}
    </>
  );
};

const InvoiceRow = ({ item }) => {
  const { toggle } = useModal();
  const { toggleSlideOver } = useSlideOver();
  const {
    setObjID,
    setAdminType,
    setAdminTempWorkerId,
    setAdminUserId,
    setAdminFileName,
    setModalData,
    setTarget,
  } = UseContext();
  const { selectedIds, setSelectedIds, year, month } = useOutletContext();

  const isSelected = selectedIds.includes(item.invoice_number);
  const isImported = item.status === 'imported';

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const dropDownRef = useRef(null);
  const dotsRef = useRef(null);

  const updateDropdownPos = useCallback(() => {
    if (dotsRef.current) {
      const rect = dotsRef.current.getBoundingClientRect();
      const menuHeight = isImported ? 120 : 40;
      const fitsBelow = rect.bottom + 4 + menuHeight < window.innerHeight;
      setDropdownPos({
        top: fitsBelow ? rect.bottom + 4 : rect.top - menuHeight - 4,
        left: rect.right - 192,
      });
    }
  }, [isImported]);

  useEffect(() => {
    const handler = (e) => {
      if (
        dropDownRef.current && !dropDownRef.current.contains(e.target) &&
        dotsRef.current && !dotsRef.current.contains(e.target)
      )
        setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleCheckbox = () => {
    setSelectedIds((prev) =>
      isSelected
        ? prev.filter((id) => id !== item.invoice_number)
        : [...prev, item.invoice_number]
    );
  };

  const handleRowClick = () => {
    if (!item.id_company) return;
    setModalData({ invoiceYear: year, invoiceMonth: month });
    setTarget('invoices');
    setObjID(item.id_company);
    toggleSlideOver('get_company');
  };

  const handleViewInvoice = () => {
    setDropdownOpen(false);
    const fileName = `invoice_${item.id_company_bestt}_${item.invoice_number}`;
    setAdminType('invoice');
    setAdminTempWorkerId('');
    setAdminUserId(item.id_company);
    setAdminFileName(fileName);
    toggle('ad_file_view');
  };

  const handleImport = () => {
    setDropdownOpen(false);
    setModalData({ invoices: [item], year, month });
    toggle('invoice_import_unit');
  };

  const handleDelete = () => {
    setDropdownOpen(false);
    setModalData({ invoices: [item], year, month });
    toggle('invoice_delete');
  };

  const [pushingPL, setPushingPL] = useState(false);
  const handlePushPennyLane = () => {
    setDropdownOpen(false);
    const token = localStorage.getItem('xsrfToken');
    setPushingPL(true);
    ApiDatabase.pushInvoicesToPennyLane(
      { token, invoice_numbers: [item.invoice_number] },
      (data) => {
        setPushingPL(false);
        if (data.created > 0) toast.success(`Facture ${item.invoice_number} envoyée sur PennyLane`);
        if (data.failed > 0) toast.warning(`Erreur pour la facture ${item.invoice_number}`);
      },
      (err) => {
        setPushingPL(false);
        toast.error(err.response?.data?.error || 'Erreur lors de l\'envoi vers PennyLane');
      }
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const [y, m, d] = dateStr.slice(0, 10).split('-');
      return `${d}/${m}/${y}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-2 py-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={handleCheckbox}
          className="cursor-pointer"
        />
      </td>
      <td
        className="px-2 py-3 cursor-pointer"
        onClick={handleRowClick}
      >
        {item.invoice_number}
      </td>
      <td
        className="px-2 py-3 cursor-pointer"
        onClick={handleRowClick}
      >
        {item.company_name}
      </td>
      <td
        className="px-2 py-3 cursor-pointer"
        onClick={handleRowClick}
      >
        {formatDate(item.date_facture)}
      </td>
      <td
        className="px-2 py-3 cursor-pointer"
        onClick={handleRowClick}
      >
        {item.amount ? `${Number(item.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-'}
      </td>
      <td
        className="px-2 py-3 cursor-pointer"
        onClick={handleRowClick}
      >
        {item.remaining_amount != null
          ? `${Number(item.remaining_amount).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
          : <span className="text-gray-400 flex justify-center">-</span>}
      </td>
      <td className="px-2 py-3">
        <BadgeSquare type={isImported ? 12 : 13} />
      </td>
      <td className="px-2 py-3">
        {isImported ? (
          <BadgeSquare type={STATUS_BADGE_MAP[item.paid_status] ?? 21} />
        ) : (
          <span className="text-gray-400 flex justify-center">-</span>
        )}
      </td>
      <td className="px-2 py-3">
        <div
          className="cursor-pointer w-9 h-9 flex items-center justify-center rounded hover:bg-gray-100"
          ref={dotsRef}
          onClick={() => {
            updateDropdownPos();
            setDropdownOpen(!dropdownOpen);
          }}
        >
          <Dots w={14} h={4} color="#374151" />
        </div>
        {dropdownOpen && createPortal(
          <div
            ref={dropDownRef}
            className="fixed border-2 border-[#F3F4F6] rounded-lg shadow-lg w-48 bg-gray-50 z-[9999]"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            {!isImported ? (
              <div
                className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                onClick={handleImport}
              >
                Importer
              </div>
            ) : (
              <>
                <div
                  className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 text-gray-900"
                  onClick={handleViewInvoice}
                >
                  Consulter la facture
                </div>
                <div
                  className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                  onClick={handleImport}
                >
                  Importer à nouveau
                </div>
                <div className="border-b border-gray-100" />
                <div
                  className={`flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 ${pushingPL ? 'opacity-50 pointer-events-none' : ''}`}
                  onClick={handlePushPennyLane}
                >
                  {pushingPL
                    ? 'Envoi en cours...'
                    : item.paid_status && item.paid_status !== 'to_import_on_pennylane'
                      ? 'Réimporter sur PennyLane'
                      : 'Exporter vers PennyLane'}
                </div>
                <div className="border-b border-gray-100" />
                <div
                  className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 text-red-500"
                  onClick={handleDelete}
                >
                  Supprimer
                </div>
              </>
            )}
          </div>,
          document.body
        )}
      </td>
    </tr>
  );
};

export default TbodyInvoiceAdmin;
